





















































































import { Component, Vue } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';
import type { User } from '@/models/user';
import SideBar from '@/components/layout/SideBar.vue';
import AppSwitcher from '@/components/layout/AppSwitcher.vue';
import TopBarContainer from '@/components/layout/TopBarContainer.vue';
import AppMessageToast from '@/components/system/AppMessageToast.vue';
import LanguageChooser from '@/components/utils/LanguageChooser.vue';
import { userWebservice } from '@/webservices/user';
import { phaseWebservice } from '@/webservices/phase';
import { tradeWebservice } from '@/webservices/trade';
import { pmPhaseWebservice } from '@/webservices/pmphase';
import { conceptWebservice } from '@/webservices/concept';
import { appMessageWebservice } from '@/webservices/appMessage';

import { activeModal } from '@/helpers/modal';

import type { Phase } from '@/models/phase';
import type { Trade } from '@/models/trade';
import type { PMPhase } from '@/models/pmphase';
import type { Concept } from '@/models/concept';
import type { AppMessage } from '@/models/appMessage';
import type { XProcess } from '@/models/xprocess';
import { xProcessWebservice } from '@/webservices/xprocess';
import UserScopeSelect from '@/components/layout/UserScopeSelect.vue';
import { hasAnyPermission, hasPermission } from '@/helpers/auth/permissionHelpers';
import { PERMISSIONS } from '@/helpers/auth/permissions';
import { approvePermissions } from '@/helpers/auth/commonPermissions';

@Component({
    name: 'App',
    components: {
        UserScopeSelect,
        SideBar,
        AppMessageToast,
        TopBarContainer,
        AppSwitcher,
        LanguageChooser,
    },
})
export default class App extends Vue {
    @Getter('account/userInfo') private userInfo: User;
    @Getter('account/loggedIn') private loggedIn: boolean;

    @Getter('appMessage/closedMessageIds') private closedMessageIds: number[];

    @Action('account/fetchCompanyByUser') private fetchCompany;
    @Action('approval/fetchAllOpen') private fetchAllOpenLessons;
    @Action('worksheet/fetchAllToDos') private fetchAllToDos;
    @Action('notifications/fetchCount') private fetchNotificationsCount;
    @Action('company/fetchAllDynamicFields') private fetchAllDynamicFields: any;

    private activeModal = activeModal;

    private phases = new Array<Phase>();
    private originalTrades = new Array<Trade>();
    private originalPhases = new Array<PMPhase>();
    private originalProcesses = new Array<XProcess>();
    private appMessages = new Array<AppMessage>();
    private originalConcepts = new Array<Concept>();
    private loadOpenLessonsInterval: NodeJS.Timeout;
    private loadToDosInterval: NodeJS.Timeout;
    private loadNotificationsCountInterval: NodeJS.Timeout;
    private darkMode: boolean = false;
    private appSwitcherActive: boolean = true;

    // Mounted is called initially
    private mounted() {
        document.documentElement.lang = this.$i18n.locale;
        this.load();
    }

    private get isLocalhost() {
        return process.env.VUE_APP_BASE_DOMAIN_URL === 'localhost';
    }

    private set isLoading(value: boolean) {
        this.$store.commit('utils/setIsLoading', value);
    }

    private get isLoading() {
        return this.$store.getters['utils/isLoading'];
    }

    private load() {
        if (this.loggedIn) {
            this.getUserInfo();
            this.loadKnowledge();
            this.fetchAllDynamicFields();
        } else {
            this.isLoading = false;
        }
    }

    private closeAppMessageToast(messageId: number) {
        this.$store.commit('appMessage/setClosedMessageId', messageId);
    }

    private getUserInfo() {
        userWebservice.getUserInfo().then((getUserInfoResponse) => {
            this.$store.commit('account/setUserInfo', getUserInfoResponse.data);
            // Fetch the company and store it
            this.fetchCompany();
            if (hasAnyPermission(approvePermissions)) {
                // Fetch all open lessons (this is also needed for the sidebar counter)
                this.fetchAllOpenLessons();
                this.loadOpenLessonsInterval = setInterval(async () => {
                    this.fetchAllOpenLessons();
                }, 600 * 1000);
            }

            if (hasPermission(PERMISSIONS.APPEX_REPORT)) {
                this.fetchAllToDos();
                this.loadToDosInterval = setInterval(async () => {
                    this.fetchAllToDos();
                }, 600 * 1000);
            }

            if (hasPermission(PERMISSIONS.APPEX_NOTIFICATION)) {
                this.fetchNotificationsCount();
                this.loadNotificationsCountInterval = setInterval(async () => {
                    this.fetchNotificationsCount();
                }, 600 * 1000);
                this.fetchNotificationsCount();
                this.loadNotificationsCountInterval = setInterval(async () => {
                    this.fetchNotificationsCount();
                }, 600 * 1000);
            }
        });
    }

    private async loadKnowledge() {
        this.isLoading = true;

        const promises: Array<Promise<any>> = [];

        promises.push(this.loadTrades());
        promises.push(this.loadPhases());
        promises.push(this.loadPmPhases());
        promises.push(this.loadXProcesses());

        if (hasPermission(PERMISSIONS.APPEX_TOPIC_GLOBAL)) {
            promises.push(this.loadConcepts());
        }

        promises.push(this.loadAppMessages());

        await Promise.all(promises);

        setTimeout(() => {
            this.isLoading = false;
        }, 100);
    }

    private async loadTrades() {
        try {
            const response = await tradeWebservice.load(true);
            this.originalTrades = response.data;
        } catch (error) {
            console.error(error);
        }
    }

    private async loadPhases() {
        try {
            const response = await phaseWebservice.load();
            this.phases = response.data;
        } catch (error) {
            console.error(error);
        }
    }

    private async loadPmPhases() {
        try {
            const response = await pmPhaseWebservice.load(true);
            this.originalPhases = response.data;
        } catch (error) {
            console.error(error);
        }
    }

    private async loadXProcesses() {
        try {
            const response = await xProcessWebservice.load(true);
            this.originalProcesses = response.data;
        } catch (error) {
            console.error(error);
        }
    }

    private async loadConcepts() {
        this.originalConcepts = await conceptWebservice.load();
    }

    private async loadAppMessages() {
        this.appMessages = await appMessageWebservice.load();
    }

    private destroyed() {
        clearInterval(this.loadOpenLessonsInterval);
        clearInterval(this.loadToDosInterval);
        clearInterval(this.loadNotificationsCountInterval);
    }

    private toggleDarkMode() {
        this.darkMode = !this.darkMode;
    }
}
